// External Packages
import { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BsArrowRightSquare } from "react-icons/bs";
import { BsArrowLeftSquare } from "react-icons/bs";
import { BsPlusLg } from "react-icons/bs";
import { MdRefresh } from "react-icons/md";
import { BiDownload } from "react-icons/bi";

// Internal Packages
import { DataContext } from "../../context/data-context";
import { arrayToCsv } from "../../util/arrayToCsv";
import {
  timestampToDate,
  timestampToString,
} from "../../util/timestamp-to-date";
import Radio from "../UI/Radio";
import BookingCard from "./BookingCard";
import LoadingSpinnerWrapper from "../UI/LoadingSpinnerWrapper";
import SearchBar from "../UI/SearchBar";
import Heading from "../UI/Heading";

export default function BookingsPage() {
  const navigate = useNavigate();
  const { bookings, loadingData, fetchBookings, vehicles, customers } =
    useContext(DataContext);

  // Initialize States
  const [selected, setSelected] = useState("Active");
  const [filteredBookings, setFilteredBookings] = useState([]);

  // ======================== FILTER HANDLER
  const filterBookings = (selected) => {
    if (selected === "Active") {
      const arrayCopy = bookings.filter(
        (booking) => booking.status === "active"
      );
      arrayCopy.sort((a, b) => {
        return timestampToDate(a.end_date) - timestampToDate(b.end_date);
      });
      setFilteredBookings(arrayCopy);
    } else if (selected === "Upcoming") {
      const arrayCopy = bookings.filter(
        (booking) =>
          booking.status === "confirmed" || booking.status === "pending"
      );
      arrayCopy.sort((a, b) => {
        return timestampToDate(a.end_date) - timestampToDate(b.end_date);
      });
      setFilteredBookings(arrayCopy);
    } else if (selected === "Completed") {
      const arrayCopy = bookings.filter(
        (booking) =>
          booking.status === "completed" || booking.status === "cancelled"
      );
      arrayCopy.sort((a, b) => {
        return timestampToDate(b.end_date) - timestampToDate(a.end_date);
      });
      setFilteredBookings(arrayCopy);
    }
  };

  // ======================== SEARCH HANDLER
  const searchBookings = (query) => {
    filterBookings(selected);
    setFilteredBookings(filteredBookings.filter((booking) => {
      let match = true;
      const string = `${booking.customer.firstName} ${booking.customer.lastName} 
      ${booking.vehicle.make} ${booking.vehicle.model} ${booking.vehicle.year}
      ${booking.location}`.toLowerCase()
      query.split(" ").forEach(word => {
        if (!string.includes(word.toLowerCase())) match = false;
      })
      return match;
    }));
  };

  // ======================== REFRESH HANDLER
  const refreshHandler = () => {
    fetchBookings();
  };

  // ======================== DOWNLOAD CSV
  const downloadCSV = () => {
    if (!bookings) return;
    const formatted = [...bookings];
    formatted.forEach((booking) => {
      const vehicle = vehicles.find(
        (vehicle) => vehicle.id === booking.vehicle?.id
      );
      booking.vin = vehicle?.vin;
      booking.date_created = timestampToString(
        booking.date_created,
        "MM/dd/yyyy hh:mm"
      );
      booking.start_date = timestampToString(
        booking.start_date,
        "MM/dd/yyyy hh:mm"
      );
      booking.end_date = timestampToString(
        booking.end_date,
        "MM/dd/yyyy hh:mm"
      );
      booking.vehicleId = booking.vehicle?.id;
      booking.vehicle = `${booking.vehicle.make} ${booking.vehicle.model} ${booking.vehicle.year}`;
      booking.delivery = false;
      booking.price = booking.price?.total;
      booking.customerId = booking.customer?.id;
      booking.customer = `${booking.customer.firstName} ${booking.customer.lastName}`;
    });
    arrayToCsv(formatted, "bookings");
  };

  useEffect(() => {
    filterBookings(selected);
  }, [selected]);

  return (
    <div className="container">
      <Heading title="Bookings">
        <BiDownload
          onClick={downloadCSV}
          className="text-2xl text-white cursor-pointer hover:text-stone-400"
          title="Download CSV"
        />
        <BsPlusLg
          onClick={() => navigate("/bookings/add")}
          className="text-lg text-white cursor-pointer hover:text-stone-400"
        />
        <MdRefresh
          onClick={refreshHandler}
          className="text-2xl text-white cursor-pointer hover:text-stone-400"
        />
      </Heading>

      <SearchBar
        searchHandler={searchBookings}
        clearSearch={() => filterBookings(selected)}
      />

      <div className="grid grid-cols-3 gap-4 h-fit">
        <Radio
          selected={selected}
          setSelected={setSelected}
          options={["Active", "Upcoming", "Completed"]}
        />
      </div>

      <div className="list">
        {loadingData ? (
          <LoadingSpinnerWrapper />
        ) : (
          <div className="grid gap-8">
            {filteredBookings.map((booking) => (
              <BookingCard
                booking={booking}
                key={booking.id}
                customer={
                  customers.filter((c) => c.id === booking.customer.id)[0]
                }
              />
            ))}
            {filteredBookings.length === 0 && (
              <p className="my-4 text-stone-400">No bookings found</p>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center justify-end space-x-4 text-sm text-stone-200">
        <p className="text-stone-400">
          Showing {filteredBookings.length} of {filteredBookings.length}{" "}
        </p>
        <BsArrowLeftSquare className="inline-block text-xl" />
        <BsArrowRightSquare className="inline-block text-xl" />
      </div>
    </div>
  );
}
